import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import Flex from "../../../../../components/Flex";
import StatusChip from "../../../../../components/StatusChip";
import StyledDialog from "../../../../../components/StyledDialog";
import Text from "../../../../../components/Text";

interface CodeListCellProps {
  /**
   * Codes from the API. Usually a comma-separated string ("99213, 99214"),
   * but tolerated as a single number, an array, or an object/map as well.
   */
  codes?: unknown;
  /** Heading shown in the modal, e.g. "CPT Codes" / "ICD-10 Codes". */
  title: string;
}

const CHIP_BG = "#F2F4F7";
const CHIP_COLOR = "#373D41";

// Normalises whatever the BE sends into a clean, flat list of code strings.
// Handles: comma-separated string ("93000, 97826"), an array whose element(s)
// are themselves comma-separated (["93000, 97826"]), an object/map of codes,
// or a single number. Every chunk is split on commas and flattened.
const parseCodes = (codes?: unknown): string[] => {
  if (codes == null) return [];

  const chunks = Array.isArray(codes)
    ? codes
    : typeof codes === "object"
      ? Object.values(codes as Record<string, unknown>)
      : [codes];

  return chunks
    .flatMap((chunk) => String(chunk).split(","))
    .map((code) => code.trim())
    .filter(Boolean);
};

/**
 * Generic table-cell renderer for comma-separated code strings (CPT, ICD-10, …).
 * Shows the first code inline; when more than one exists it appends a clickable
 * "+N" chip that opens a modal listing every code in a Sr. No table.
 *
 * Reusable across columns — pass a different `title` per code type.
 */
const CodeListCell = ({ codes, title }: CodeListCellProps) => {
  const [open, setOpen] = useState(false);

  const codeList = parseCodes(codes);

  if (codeList.length === 0) return <Text isTabletext>-</Text>;

  const [firstCode, ...rest] = codeList;
  const remaining = rest.length;

  return (
    <>
      <Flex gap="4px" alignItems="center" flexWrap="wrap">
        <StatusChip
          label={firstCode}
          bgColor={CHIP_BG}
          color={CHIP_COLOR}
          sx={{
            maxWidth: "150px",
            "& .MuiChip-label": { textAlign: "left" },
          }}
        />
        {remaining > 0 && (
          <Box
            component="span"
            sx={{ cursor: "pointer", display: "inline-flex" }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <StatusChip
              label={`+${remaining}`}
              bgColor={CHIP_BG}
              color={CHIP_COLOR}
              sx={{ cursor: "pointer" }}
            />
          </Box>
        )}
      </Flex>

      <StyledDialog
        open={open}
        onClose={() => setOpen(false)}
        dialogTitle={title}
        PaperProps={{ sx: { width: "360px" } }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="80px">Sr. No</TableCell>
              <TableCell>Code</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {codeList.map((code, index) => (
              <TableRow key={`${code}-${index}`}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{code}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledDialog>
    </>
  );
};

export default CodeListCell;
